/**
 * Representantes Service
 *
 * Epic 45: Email é agora o identificador único. CPF/CNPJ é opcional.
 */
import apiClient from './apiClient';

export interface Representante {
  id: number;
  nome: string;
  cpf_cnpj?: string | null;  // Epic 45: Agora opcional
  telefone: string;
  celular?: string;
  email: string;  // Epic 45: Identificador único (obrigatório)
  empresa?: string;
  cliente_razao_social?: string;
  cliente_nome_fantasia?: string;
  observacoes?: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  hospitais_count?: number; // Contagem de hospitais vinculados
}

export interface RepresentanteCreate {
  nome: string;
  cpf_cnpj?: string | null;  // Epic 45: Agora opcional
  telefone: string;
  celular?: string;
  email: string;  // Epic 45: Identificador único (obrigatório)
  empresa?: string;
  observacoes?: string;
}

export interface HospitalAssociation {
  id: number;
  hospital_id: number;
  hospital_nome: string;
  representante_id: number;
  representante_nome: string;
  data_inicio: string;
  data_fim?: string;
  ativo: boolean;
}

export interface ImportResult {
  success: boolean;
  created: number;
  updated: number;
  vinculos_criados: number;
  errors: string[];
  warnings: string[];
  total_linhas_processadas: number;
}

export const representantesService = {
  async getAll(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    hospital_id?: number;
    ativo?: boolean;
  }) {
    const response = await apiClient.get('/representantes/', { params });
    return response.data;
  },

  async getByCliente(clienteId: number): Promise<Representante[]> {
    const response = await apiClient.get(`/clientes/${clienteId}/representantes`);
    return response.data;
  },

  async getById(id: number): Promise<Representante> {
    const response = await apiClient.get(`/representantes/${id}`);
    return response.data;
  },

  async create(data: RepresentanteCreate): Promise<Representante> {
    const response = await apiClient.post('/representantes/', data);
    return response.data;
  },

  async update(id: number, data: Partial<RepresentanteCreate>): Promise<Representante> {
    const response = await apiClient.put(`/representantes/${id}`, data);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await apiClient.delete(`/representantes/${id}`);
  },

  async getHospitaisAssociados(repId: number): Promise<HospitalAssociation[]> {
    const response = await apiClient.get(`/representantes/${repId}/hospitais`);
    return response.data;
  },

  async addHospital(repId: number, hospitalId: number): Promise<void> {
    await apiClient.post(`/representantes/${repId}/hospitais/${hospitalId}`, {});
  },

  async removeHospital(repId: number, hospitalId: number): Promise<void> {
    await apiClient.delete(`/representantes/${repId}/hospitais/${hospitalId}`);
  },

  /**
   * Importa representantes via Excel (.xlsx)
   */
  async importExcel(file: File): Promise<ImportResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/representantes/import-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  },

  /**
   * Baixa template Excel para importação
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get('/representantes/template-excel', {
      responseType: 'blob'
    });
    return response.data;
  }
};
